import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { PageLayout } from '../components/ui/Layout';
import { Card } from '../components/ui/Card';
import { PrimaryButton, SecondaryButton } from '../components/ui/Button';
import { DEFAULT_QUIZ } from '../data/defaultQuiz';
import { doGenderNameReplacements } from '../utils/scoreCalculator';
import BrevoForm from '../components/BrevoForm';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const { FiDownload, FiRefreshCw, FiCheckCircle } = FiIcons;

// Helper to render simple HTML tags safely in the UI (currently supports <i>)
const renderSafeHtml = (text) => {
  if (!text) return null;
  // Split by <i> tag capturing the content
  const parts = text.split(/(<i>.*?<\/i>)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('<i>') && part.endsWith('</i>')) {
      // Remove <i> tags and render as italic
      const content = part.substring(3, part.length - 4);
      return <i key={index} className="italic">{content}</i>;
    }
    // Return plain text
    return part;
  });
};

// Helper to strip HTML tags for PDF export
const stripHtml = (text) => {
  if (!text) return '';
  return text.replace(/<[^>]*>?/gm, '');
};

export default function QuizResults() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Safely access results from location state
  const results = location.state?.results;

  useEffect(() => {
    // Redirect to home if results are missing or invalid (e.g. missing score)
    if (!results || !results.score) {
        // Use a slight delay to ensure navigation happens after mount
        const timer = setTimeout(() => {
            navigate('/', { replace: true });
        }, 0);
        return () => clearTimeout(timer);
    }
  }, [results, navigate]);

  // Prevent rendering if results are invalid to avoid crashes before redirect
  if (!results || !results.score) return null;

  const { 
    score,
    classificationText,
    scoreDetail,
    questionAnswerDetail,
    scoreString, 
    bestTypeLabel,
    userDiagnosis,
    buyerName,
    buyerGender,
    meetingIndex
  } = results;
  
  const scoreNarrative = scoreDetail && scoreDetail.length > 0 ? scoreDetail[0] : null;
  
  const tipsList = [];
  if (scoreDetail && scoreDetail.length > 1) {
      tipsList.push(...scoreDetail.slice(1));
  }
  if (questionAnswerDetail && questionAnswerDetail.length > 0) {
      tipsList.push(...questionAnswerDetail);
  }

  const handleExportAssessment = (e) => {
    if (e) e.preventDefault();
    
    try {
        if (!results) {
            alert("No results data available to export.");
            return;
        }

        console.log("Generating PDF Report...");
        const doc = new jsPDF();
        const lineHeight = 7;
        let cursorY = 20;

        // --- Header ---
        doc.setFontSize(20);
        doc.setTextColor(2, 112, 169); // Primary Blue
        doc.text("INTIMASEE Meeting Assessment", 14, cursorY);
        cursorY += 10;

        // --- Metadata ---
        doc.setFontSize(10);
        doc.setTextColor(100);
        
        // Date format: Month, Day, Year
        const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
        const date = new Date().toLocaleDateString('en-US', dateOptions);
        
        const buyer = buyerName || 'Unknown';
        const meeting = meetingIndex === '1' ? '1st Meeting' : meetingIndex === '2' ? '2nd Meeting' : '3rd+ Meeting';
        
        doc.text(`Date: ${date}`, 14, cursorY);
        doc.text(`Buyer Name: ${buyer}`, 80, cursorY);
        doc.text(`Meeting: ${meeting}`, 160, cursorY);
        cursorY += 15;

        // --- Scoring Results Summary ---
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text("Scoring Results", 14, cursorY);
        cursorY += 8;

        const userDiagLabel = DEFAULT_QUIZ.typeDefinitions.find(t => t.id === userDiagnosis)?.label || userDiagnosis || 'N/A';
        const typeVal = bestTypeLabel || score?.bestType || '';

        autoTable(doc, {
            startY: cursorY,
            head: [['Metric', 'Value']],
            body: [
                ['Partnership Score', score?.pScore ?? ''],
                ['Relationship Type', typeVal],
                // Result Code row removed
                ['Your Assessment', userDiagLabel],
                ['INTIMASEE Assessment', scoreString],
            ],
            theme: 'striped',
            headStyles: { fillColor: [2, 112, 169] },
            styles: { fontSize: 10 },
            margin: { left: 14, right: 14 }
        });
        
        cursorY = doc.lastAutoTable.finalY + 15;

        // --- Assessment Narrative ---
        doc.setFontSize(14);
        doc.text("Assessment", 14, cursorY);
        cursorY += 8;

        const narrativeText = stripHtml(classificationText || '') + (scoreNarrative ? "\n\n" + stripHtml(scoreNarrative) : "");
        
        // Use autoTable for text wrapping convenience
        autoTable(doc, {
            startY: cursorY,
            body: [[narrativeText]],
            theme: 'plain',
            styles: { 
                fontSize: 10, 
                cellPadding: 0, 
                overflow: 'linebreak',
                textColor: 60
            },
            margin: { left: 14, right: 14 },
            didDrawCell: (data) => {
                // No border
            }
        });
        cursorY = doc.lastAutoTable.finalY + 10;

        // --- Tips ---
        if (tipsList.length > 0) {
            // Check for page break
            if (cursorY > 250) {
                doc.addPage();
                cursorY = 20;
            }

            doc.setFontSize(14);
            doc.setTextColor(0);
            doc.text("Tips for Future Interactions", 14, cursorY);
            cursorY += 8;

            const tipsBody = tipsList.map(t => [`• ${stripHtml(t)}`]);

            autoTable(doc, {
                startY: cursorY,
                body: tipsBody,
                theme: 'plain',
                styles: { fontSize: 10, cellPadding: 2, textColor: 60 },
                margin: { left: 14, right: 14 }
            });
            cursorY = doc.lastAutoTable.finalY + 15;
        }

        // --- Question Responses ---
        // Check for page break
        if (cursorY > 250) {
            doc.addPage();
            cursorY = 20;
        }

        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text("Question Responses", 14, cursorY);
        cursorY += 8;

        const questionsRows = [];
        if (results.answers) {
            Object.entries(results.answers).forEach(([qid, val]) => {
                if (val === null || val === undefined) return;
                
                // Find question in default or branches
                let q = DEFAULT_QUIZ.questions.find(item => item.id === qid);
                if (!q) {
                    q = DEFAULT_QUIZ.branches.a.find(item => item.id === qid) || 
                        DEFAULT_QUIZ.branches.b.find(item => item.id === qid);
                }
                
                if (q) {
                    const qText = doGenderNameReplacements(q.questionText, null, buyer, buyerGender);
                    const left = doGenderNameReplacements(q.leftLabel, null, buyer, buyerGender);
                    const right = doGenderNameReplacements(q.rightLabel, null, buyer, buyerGender);

                    let answerText = val;
                    if (q.isBinary) {
                        answerText = val < 0 ? left : right;
                    } else {
                        answerText = `${val} (Scale: ${left} to ${right})`;
                    }
                    
                    questionsRows.push([stripHtml(qText), stripHtml(String(answerText))]);
                }
            });
        }

        autoTable(doc, {
            startY: cursorY,
            head: [['Question', 'Selected Answer']],
            body: questionsRows,
            theme: 'grid',
            headStyles: { fillColor: [100, 116, 139] },
            styles: { fontSize: 9, cellPadding: 3 },
            columnStyles: {
                0: { cellWidth: 100 }, // Question column width
                1: { cellWidth: 'auto' }
            },
            margin: { left: 14, right: 14 }
        });

        // --- Save ---
        const fileName = `INTIMASEE_Assessment_${buyer.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().slice(0,10)}.pdf`;
        doc.save(fileName);
        
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to generate PDF file. Please try again.');
    }
  };

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto pb-8 md:pb-12 space-y-4 md:space-y-6">

        {/* Meeting Header */}
        <div className="text-center mb-4 md:mb-6">
             <div className="inline-block bg-blue-100 text-[#0270a9] px-3 py-1 md:px-4 md:py-1.5 rounded-full text-xs md:text-sm font-semibold mb-2">
                {results.meetingIndex === '1' ? '1st Meeting' : 
                 results.meetingIndex === '2' ? '2nd Meeting' : '3rd+ Meeting'}
             </div>
             <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Interaction Assessment</h1>
        </div>

        {/* Diagnosis Comparison Card */}
        <Card className="p-4 md:p-6 bg-slate-50 border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
                <div className="bg-white p-3 md:p-4 rounded-lg border border-slate-200 shadow-sm">
                    <p className="text-xs md:text-sm text-slate-500 mb-1">You assessed your interaction as:</p>
                    <div className="text-lg md:text-xl font-bold text-slate-800">
                        {userDiagnosis === 'A' && 'Ad-hoc'}
                        {userDiagnosis === 'S' && 'Social'}
                        {userDiagnosis === 'T' && 'Technical'}
                        {userDiagnosis === 'P' && 'Partnership'}
                    </div>
                </div>
                <div className="bg-white p-3 md:p-4 rounded-lg border border-[#0270a9] shadow-sm relative overflow-hidden">
                    <p className="text-xs md:text-sm text-slate-500 mb-1">The assessment indicates the interaction was:</p>
                    <div className="flex items-baseline gap-2">
                        <span className="text-2xl md:text-3xl font-bold text-[#0270a9]">{scoreString}</span>
                        <span className="text-sm md:text-lg font-medium text-[#0270a9]">({bestTypeLabel})</span>
                    </div>
                    {userDiagnosis === score.bestType && (
                        <div className="mt-2 flex items-center gap-1.5 text-green-600 text-xs md:text-sm font-medium">
                            <SafeIcon icon={FiCheckCircle} />
                            <span>Your perception matches the data!</span>
                        </div>
                    )}
                </div>
            </div>
        </Card>

        {/* Main Results Card */}
        <Card className="p-5 md:p-8 space-y-6 md:space-y-8">
          
          {/* Section 1: Assessment Narrative */}
          <section>
             <h2 className="text-xl md:text-2xl font-bold text-slate-900 border-b pb-2 md:pb-3 mb-3 md:mb-4 border-slate-100">
                Assessment
             </h2>
             <div className="prose prose-sm md:prose-base text-slate-700 max-w-none space-y-3 md:space-y-4">
                 <p className="font-medium bg-blue-50 p-3 md:p-4 rounded-lg border border-blue-100">
                    {renderSafeHtml(classificationText)}
                 </p>
                 
                 {scoreNarrative && (
                     <p className="whitespace-pre-line leading-relaxed">
                        {renderSafeHtml(scoreNarrative)}
                     </p>
                 )}
             </div>
          </section>

          {/* Section 2: Tips */}
          {tipsList.length > 0 && (
              <section>
                 <h2 className="text-xl md:text-2xl font-bold text-slate-900 border-b pb-2 md:pb-3 mb-3 md:mb-4 border-slate-100">
                    Tips for future interactions
                 </h2>
                 <ul className="space-y-3 md:space-y-4">
                    {tipsList.map((tip, idx) => (
                        <li key={idx} className="flex gap-3 md:gap-4 items-start bg-slate-50 p-3 md:p-4 rounded-lg">
                            <div className="mt-1 flex-shrink-0 w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#0270a9]" />
                            <p className="text-sm md:text-base text-slate-700 leading-relaxed">
                                {renderSafeHtml(tip)}
                            </p>
                        </li>
                    ))}
                 </ul>
              </section>
          )}

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-4 md:pt-6 border-t border-slate-100">
             <SecondaryButton 
                type="button"
                onClick={handleExportAssessment} 
                className="flex items-center justify-center gap-2"
             >
                 <SafeIcon icon={FiDownload} />
                 <span>Download PDF Report</span>
             </SecondaryButton>
             <PrimaryButton 
                onClick={() => navigate('/')} 
                className="flex items-center justify-center gap-2"
             >
                 <SafeIcon icon={FiRefreshCw} />
                 <span>Start New Assessment</span>
             </PrimaryButton>
          </div>

        </Card>

        {/* Brevo Form Section */}
        <Card className="p-4 md:p-6 bg-white border-slate-200">
            {/* Header removed as requested */}
            <BrevoForm />
        </Card>

      </div>
    </PageLayout>
  );
}